import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Inbox as InboxIcon, Send, FileEdit, Archive, RefreshCw, PenSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import ThreadList from "@/components/inbox/thread-list";
import ThreadDetail from "@/components/inbox/thread-detail";
import ComposeNewDialog from "@/components/inbox/compose-new-dialog";
import { inboxApi } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useTitle } from "@/hooks/useTitle";
import type { InboxFolder } from "@/types/api";

const FOLDERS: { value: InboxFolder; label: string; icon: typeof InboxIcon }[] = [
  { value: "Inbox", label: "Inbox", icon: InboxIcon },
  { value: "SentItems", label: "Sent", icon: Send },
  { value: "Drafts", label: "Drafts", icon: FileEdit },
  { value: "Archive", label: "Archive", icon: Archive },
];

export default function InboxRoute() {
  useTitle("Inbox");
  const { conversationId } = useParams();
  const [folder, setFolder] = useState<InboxFolder>("Inbox");
  const [composeOpen, setComposeOpen] = useState(false);
  const qc = useQueryClient();

  const threadsQuery = useQuery({
    queryKey: ["inbox", "threads", folder],
    queryFn: () => inboxApi.threads(folder),
    refetchInterval: 60_000,
  });

  const mirrorMutation = useMutation({
    mutationFn: (force: boolean) => inboxApi.mirrorNow(force),
    onSettled: () => qc.invalidateQueries({ queryKey: ["inbox"] }),
  });

  return (
    <div className="grid h-full grid-cols-[260px_minmax(0,1fr)_minmax(0,1.4fr)] divide-x divide-line">
      {/* Folder list */}
      <aside className="flex h-full flex-col bg-surface-panel">
        <div className="flex h-[44px] items-center gap-1 border-b border-line px-3">
          <span className="font-display text-sm font-semibold text-ink">Mailboxes</span>
          <Button
            size="icon"
            variant="ghost"
            className="ml-auto h-7 w-7"
            disabled={mirrorMutation.isPending}
            onClick={(e) => mirrorMutation.mutate(e.shiftKey)}
            title="Sync mirror (shift-click for full 60-day re-pull)"
          >
            <RefreshCw
              className={cn("h-3.5 w-3.5", mirrorMutation.isPending && "animate-spin")}
            />
          </Button>
        </div>
        <div className="border-b border-line p-2">
          <Button size="sm" className="w-full gap-1.5" onClick={() => setComposeOpen(true)}>
            <PenSquare className="h-3.5 w-3.5" /> New email
          </Button>
        </div>
        <ul className="flex-1 space-y-0.5 px-2 py-2">
          {FOLDERS.map(({ value, label, icon: Icon }) => (
            <li key={value}>
              <button
                onClick={() => setFolder(value)}
                className={cn(
                  "flex h-8 w-full items-center gap-2.5 rounded px-2.5 text-[13px] font-medium transition-colors",
                  folder === value
                    ? "bg-brand/10 text-brand-deep"
                    : "text-ink-muted hover:bg-surface-hover hover:text-ink",
                )}
              >
                <Icon className="h-4 w-4" />
                <span>{label}</span>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      {/* Thread list */}
      <section className="flex h-full min-w-0 flex-col bg-surface-panel">
        <div className="flex h-[44px] shrink-0 items-center border-b border-line px-4">
          <span className="font-display text-sm font-semibold text-ink">
            {FOLDERS.find((f) => f.value === folder)?.label}
          </span>
          <span className="ml-2 text-xs text-ink-dim">
            {threadsQuery.data?.threads.length ?? 0}
          </span>
        </div>
        <div className="min-h-0 flex-1">
          <ThreadList
            threads={threadsQuery.data?.threads}
            isLoading={threadsQuery.isLoading}
          />
        </div>
      </section>

      {/* Thread detail */}
      <section className="flex h-full min-w-0 flex-col">
        {conversationId ? (
          <ThreadDetail conversationId={conversationId} />
        ) : (
          <EmptyDetail onCompose={() => setComposeOpen(true)} />
        )}
      </section>

      <ComposeNewDialog open={composeOpen} onOpenChange={setComposeOpen} />
    </div>
  );
}

function EmptyDetail({ onCompose }: { onCompose: () => void }) {
  return (
    <div className="flex h-full items-center justify-center bg-surface px-6 text-center">
      <div>
        <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-surface-panel2">
          <InboxIcon className="h-5 w-5 text-ink-dim" />
        </div>
        <p className="mt-3 font-display text-sm font-semibold text-ink">Select a thread</p>
        <p className="mt-1 text-xs text-ink-muted">
          Pick a conversation on the left, or start a new one.
        </p>
        <Button size="sm" className="mt-3 gap-1.5" onClick={onCompose}>
          <PenSquare className="h-3.5 w-3.5" /> New email
        </Button>
      </div>
    </div>
  );
}
